import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { applyRemunerationReturn } from 'helper/remunerationReturn';

// The changes held back, per company. Kept outside React state because two
// trees edit the same page: the remuneration cards, and the coefficients'
// editor, which opens in its own slide-over on the company page.
const draftsByCompany = new Map();
const listeners = new Set();
const NONE = [];

const readDraft = (idCompany) => draftsByCompany.get(String(idCompany)) ?? NONE;
const writeDraft = (idCompany, update) => {
  const next = update(readDraft(idCompany));
  if (next.length) draftsByCompany.set(String(idCompany), next);
  else draftsByCompany.delete(String(idCompany));
  listeners.forEach((listener) => listener());
};
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// What the api says when it refuses one: its message, or the first error of
// the coefficients' grid.
const refusalOf = (err) =>
  err?.response?.data?.message ??
  err?.response?.data?.error ??
  err?.response?.data?.errors?.[0]?.message ??
  "L'enregistrement a échoué";

/**
 * The remuneration changes of a company held back until they are saved
 * together, under one justificatif (JBWEB-1468): each modal, and the
 * coefficients' editor, stages its change instead of asking for its own.
 *
 * A change: `key` (one per setting, a new stage replaces the old one), `label`,
 * `before` and `after` (shown in the list), `form` (the editor's own state, to
 * reopen it where it was left), `preview` (a modal return the page can show
 * before it is saved; none when the api resolves labels the modal does not
 * know) and `commit(justificatif)`, a promise of the modal return.
 *
 * `draft` is what the modals get, through `modalData`.
 */
const useRemunerationDraft = (idCompany) => {
  const changes = useSyncExternalStore(subscribe, () => readDraft(idCompany));

  const stage = useCallback(
    (change) =>
      writeDraft(idCompany, (previous) => {
        const index = previous.findIndex((one) => one.key === change.key);
        if (index === -1) return [...previous, change];
        return previous.map((one, i) => (i === index ? change : one));
      }),
    [idCompany],
  );
  const remove = useCallback(
    (key) => writeDraft(idCompany, (previous) => previous.filter((one) => one.key !== key)),
    [idCompany],
  );
  const discard = useCallback(() => writeDraft(idCompany, () => []), [idCompany]);

  // Saved one after the other, each with the same justificatif: the api has a
  // route per setting. A refused change stays in the list, with its error;
  // the others are applied (`onSaved`) and leave it.
  const commit = async (justificatif, onSaved) => {
    const failed = [];
    for (const change of readDraft(idCompany)) {
      try {
        onSaved(await change.commit(justificatif));
        remove(change.key);
      } catch (err) {
        failed.push(change.key);
        stage({ ...change, error: refusalOf(err) });
      }
    }
    return failed;
  };

  const draft = useMemo(
    () => ({
      stage: (change) => stage({ ...change, error: null }),
      remove,
      // Read at the time of asking: a modal opened before a change was staged
      // still finds it.
      pending: (key) => readDraft(idCompany).find((one) => one.key === key)?.form,
    }),
    [idCompany, stage, remove],
  );

  return {
    changes,
    draft,
    discard,
    remove,
    commit,
    // The company as it will be once saved, for what the page can preview.
    preview: (info) => changes.reduce((next, change) => applyRemunerationReturn(next, change.preview), info),
    isChanged: (section) => changes.some((change) => change.section === section),
  };
};

export default useRemunerationDraft;
